import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect } from "react";

import { useGetHasOpenPlacementsNearby } from "./api/useGetHasOpenPlacementsNearby";

interface JobsEnabledCache {
  hasOpenPlacementsNearby: boolean;
  lastFetched: number;
}

const JOBS_ENABLED_CACHE_KEY = "jobs-enabled";
const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;
const THIRTY_MINUTES_IN_MS = 30 * 60 * 1000;
const ONE_HOUR_IN_MS = 60 * 60 * 1000;

export function useIsJobsEnabled() {
  const worker = useDefinedWorker();

  const marketLaunchFlagValue = useCbhFlag(CbhFeatureFlag.JOBS_NEW_MARKET_LAUNCH, {
    defaultValue: {
      enabled: false,
    },
  });

  const [cache, setCache] = useLocalStorage<JobsEnabledCache | undefined>(
    JOBS_ENABLED_CACHE_KEY,
    undefined
  );

  const isCacheValid = isDefined(cache) && Date.now() - cache.lastFetched < TWO_WEEKS_IN_MS;
  const isEnabledInCache = isCacheValid && Boolean(cache.hasOpenPlacementsNearby);

  const { data: hasOpenPlacementsNearby, isLoading } = useGetHasOpenPlacementsNearby(
    { workerId: worker.userId, useHiringWorkerTypes: true },
    {
      enabled: !isEnabledInCache,
      staleTime: THIRTY_MINUTES_IN_MS,
      cacheTime: ONE_HOUR_IN_MS,
    }
  );

  useEffect(() => {
    if (!isEnabledInCache && !isLoading && hasOpenPlacementsNearby === true) {
      setCache({
        hasOpenPlacementsNearby: Boolean(hasOpenPlacementsNearby),
        lastFetched: Date.now(),
      });
    }
  }, [hasOpenPlacementsNearby, isLoading, setCache, isEnabledInCache]);

  if (isEnabledInCache) {
    return cache.hasOpenPlacementsNearby;
  }

  if (marketLaunchFlagValue.enabled) {
    return true;
  }

  return !isLoading && Boolean(hasOpenPlacementsNearby);
}
